import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import { where } from 'firebase/firestore';
import type { SceneCategory, StoryRunDoc, StoryTemplateDoc, StoryRunVisibility } from '@spirits/shared';
import { useGameStore } from '../store/useGameStore';
import { useCollection } from '../hooks/useCollection';
import { AppHeader } from '../lobby/AppHeader';
import { NewsFeed } from '../lobby/NewsFeed';
import { functions } from '../firebase';

const startStoryCallable = httpsCallable<
  { templateId: string; visibility: StoryRunVisibility },
  { ok: boolean; storyId: string; joinCode: string }
>(functions, 'startStory');

const joinStoryCallable = httpsCallable<
  { storyId?: string; joinCode?: string },
  { ok: boolean; storyId: string }
>(functions, 'joinStory');

const CATEGORY_ORDER: { value: SceneCategory; label: string }[] = [
  { value: 'fiction', label: 'Fiction' },
  { value: 'science', label: 'Science' },
  { value: 'history', label: 'History' },
  { value: 'crime', label: 'Crime' },
];

export default function LobbyRoute() {
  const user = useGameStore((s) => s.user);
  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center text-slate-400">
        <p className="text-xl tracking-wider">connecting to the unseen…</p>
      </div>
    );
  }
  return <LobbyAuthed uid={user.uid} />;
}

function LobbyAuthed({ uid }: { uid: string }) {
  const { data: templates, loading } = useCollection<StoryTemplateDoc>('storyTemplates');
  const { data: myRuns } = useCollection<StoryRunDoc>('stories', where('participants', 'array-contains', uid));
  const [activeCategory, setActiveCategory] = useState<SceneCategory | 'all'>('all');

  const availableCategories = useMemo(
    () => CATEGORY_ORDER.filter((c) => templates.some((t) => t.category === c.value)),
    [templates],
  );

  const sortedTemplates = useMemo(
    () =>
      [...templates]
        .filter((t) => activeCategory === 'all' || t.category === activeCategory)
        .sort((a, b) => a.title.localeCompare(b.title)),
    [templates, activeCategory],
  );

  const sortedRuns = useMemo(
    () => [...myRuns].sort((a, b) => tsMillis(b.updatedAt) - tsMillis(a.updatedAt)),
    [myRuns],
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100">
      <AppHeader subtitle="You drift unseen. Start a story; steer the hearts inside it." />

      <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/40 p-4 text-sm leading-relaxed text-slate-300">
          <p className="text-slate-100 font-medium">New here? Here's the game in 20 seconds.</p>
          <p className="mt-1.5 text-slate-400">
            You're a spirit — an invisible voice in a character's head. Pick a story to start your
            own private run of it. In each scene the cast stands at a fork; whisper to them (or pick
            a road outright) to steer each one. When everyone's chosen, the story writes its next
            scene from what you did — bending toward one of its possible endings. There's no wrong
            move, only different roads.
          </p>
          <p className="mt-2 text-slate-400">
            Runs are shareable — invite friends with a link or code and steer the cast together.
          </p>
          <p className="mt-2">
            <Link to="/public" className="text-amber-200/90 hover:text-amber-100 underline-offset-2 hover:underline">
              Browse public runs →
            </Link>
          </p>
        </div>

        <JoinByCode />

        {sortedRuns.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg text-slate-300 mb-3">Your runs</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sortedRuns.map((run) => (
                <RunCard key={run.id} run={run} />
              ))}
            </div>
          </section>
        )}

        <h2 className="text-lg text-slate-300 mb-4">Start a story</h2>

        {availableCategories.length > 1 && (
          <div className="mb-5 flex flex-wrap gap-2">
            {[{ value: 'all' as const, label: 'All' }, ...availableCategories].map((c) => {
              const active = activeCategory === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setActiveCategory(c.value)}
                  className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                    active
                      ? 'border-amber-300/60 bg-amber-300/10 text-amber-100'
                      : 'border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <p className="text-slate-500 text-sm">listening for the world…</p>
        ) : sortedTemplates.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-700 p-6 text-slate-400 text-sm">
            The catalog is empty. Seed it from the{' '}
            <Link to="/admin" className="text-amber-200/90 hover:text-amber-100 underline-offset-2 hover:underline">
              admin page
            </Link>
            .
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {sortedTemplates.map((t) => (
              <TemplateCard key={t.id} template={t} />
            ))}
          </div>
        )}

        <div className="mt-8">
          <NewsFeed />
        </div>
      </main>

      <DevFooter />
    </div>
  );
}

function tsMillis(ts: unknown): number {
  if (!ts) return 0;
  if (typeof ts === 'object' && ts && 'seconds' in (ts as Record<string, unknown>)) {
    return (ts as { seconds: number }).seconds * 1000;
  }
  return 0;
}

function TemplateCard({ template }: { template: StoryTemplateDoc & { id: string } }) {
  const [picking, setPicking] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 flex flex-col">
      <p className="text-xs uppercase tracking-widest text-amber-200/80 mb-0.5">{template.category}</p>
      <h3 className="text-xl text-slate-100">{template.title}</h3>
      <p className="mt-2 text-sm text-slate-400 leading-snug flex-1">{template.blurb}</p>
      <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
        {template.endings?.length > 0 && <span>✦ {template.endings.length} possible endings</span>}
      </div>
      <button
        type="button"
        onClick={() => setPicking(true)}
        className="mt-3 self-start rounded-lg border border-amber-400/50 bg-amber-950/30 text-amber-100 hover:bg-amber-900/40 px-4 py-1.5 text-sm cursor-pointer"
      >
        Start a run →
      </button>
      {picking && <StartDialog template={template} onClose={() => setPicking(false)} />}
    </div>
  );
}

function StartDialog({
  template,
  onClose,
}: {
  template: StoryTemplateDoc & { id: string };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [visibility, setVisibility] = useState<StoryRunVisibility>('unlisted');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await startStoryCallable({ templateId: template.id, visibility });
      navigate(`/play/${res.data.storyId}`);
    } catch (e) {
      setError((e as FunctionsError)?.message ?? 'could not start');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-xl"
      >
        <h2 className="text-lg text-slate-100">Start “{template.title}”</h2>
        <p className="mt-1.5 text-sm text-slate-400">
          This mints your own private run. Choose who can join:
        </p>
        <div className="mt-4 space-y-2">
          <VisibilityOption
            label="Unlisted"
            desc="Only people you share the link or code with can join."
            selected={visibility === 'unlisted'}
            onSelect={() => setVisibility('unlisted')}
          />
          <VisibilityOption
            label="Public"
            desc="Listed on the Public Runs page — anyone can drop in and play."
            selected={visibility === 'public'}
            onSelect={() => setVisibility('public')}
          />
        </div>
        {error && <p className="mt-3 text-xs text-rose-400">{error}</p>}
        <div className="mt-5 flex justify-end gap-3 text-sm">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200"
          >
            cancel
          </button>
          <button
            type="button"
            onClick={start}
            disabled={busy}
            className="px-4 py-1.5 rounded-lg border border-amber-400/60 text-amber-200 bg-amber-900/30 hover:bg-amber-900/50 disabled:opacity-50"
          >
            {busy ? 'opening…' : 'Begin'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VisibilityOption({
  label,
  desc,
  selected,
  onSelect,
}: {
  label: string;
  desc: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
        selected
          ? 'border-amber-400/60 bg-amber-950/20'
          : 'border-slate-700 bg-slate-900/40 hover:border-slate-500'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`text-sm ${selected ? 'text-amber-100' : 'text-slate-200'}`}>{label}</span>
        {selected && <span className="text-amber-300 text-xs">✓</span>}
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
    </button>
  );
}

function RunCard({ run }: { run: StoryRunDoc & { id: string } }) {
  const complete = run.status === 'complete';
  return (
    <Link
      to={`/play/${run.id}`}
      className="block rounded-2xl border border-slate-800 bg-slate-900/40 p-4 transition-colors hover:border-slate-600"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-widest text-amber-200/70">{run.category}</p>
        <span className={`text-[10px] uppercase tracking-widest ${complete ? 'text-emerald-400/80' : 'text-slate-500'}`}>
          {complete ? 'complete ✦' : `scene ${run.sceneCount}`}
        </span>
      </div>
      <h3 className="text-lg text-slate-100 mt-0.5">{run.title}</h3>
      <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
        <span>{run.visibility === 'public' ? '◍ public' : '🔒 unlisted'}</span>
        {run.participants?.length > 1 && <span>· {run.participants.length} spirits</span>}
        {!complete && <span>· code {run.joinCode}</span>}
      </div>
    </Link>
  );
}

function JoinByCode() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function join(e: FormEvent) {
    e.preventDefault();
    const c = code.trim();
    if (!c || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await joinStoryCallable({ joinCode: c });
      navigate(`/play/${res.data.storyId}`);
    } catch (err) {
      setError((err as FunctionsError)?.message ?? 'no run with that code');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={join} className="mb-8 flex items-center gap-2">
      <input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="join a run by code…"
        maxLength={8}
        className="w-44 rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 tracking-widest uppercase"
      />
      <button
        type="submit"
        disabled={busy || !code.trim()}
        className="rounded-lg border border-slate-700 text-slate-300 hover:text-slate-100 hover:border-slate-500 px-3 py-1.5 text-sm disabled:opacity-40"
      >
        {busy ? 'joining…' : 'Join'}
      </button>
      {error && <span className="text-xs text-rose-400">{error}</span>}
    </form>
  );
}

function DevFooter() {
  const chatModel = useGameStore((s) => s.chatModel);
  const evalModel = useGameStore((s) => s.evalModel);

  return (
    <footer className="flex-none border-t border-slate-800 bg-slate-900/50 px-6 py-2 flex items-center justify-between text-xs text-slate-500">
      <div className="flex items-center gap-3">
        <span className="uppercase tracking-widest">dev</span>
        {chatModel && (
          <span className="font-mono text-slate-600">
            Model: {chatModel}{evalModel ? ` (${evalModel})` : ''}
          </span>
        )}
      </div>
      <Link to="/admin" className="px-3 py-1 rounded border border-slate-700 text-slate-400 hover:text-slate-200">
        admin →
      </Link>
    </footer>
  );
}
